// Traffic control sign faces (Ryan, Oct 30 2026) — the sign itself instead of its MUTCD code, drawn on
// a canvas: on the map, on the printed picture, and (as PNGs sent with Print) on the PDF's sign list
// and load-up list. Orange diamonds for warning signs, the orange END ROAD WORK panel, and the white
// No Right Turn square. Close to the real faces, not a sign shop's artwork.

const ORANGE = '#f7931e', BLACK = '#111', RED = '#d71920';

// The words on each sign, line by line, the way the sign lays them out.
function wordsFor(code, text) {
  const c = String(code || '').toUpperCase();
  const side = /L$/.test(c) ? 'LEFT' : 'RIGHT';
  if (c === 'W20-1') return ['ROAD', 'WORK', 'AHEAD'];
  if (c.startsWith('W20-5')) return [`${side} LANE`, 'CLOSED', 'AHEAD'];
  if (c === 'W21-5') return ['SHOULDER', 'CLOSED'];   // the plain one, no side (Ryan, Oct 31 2026)
  if (c.startsWith('W21-5')) return [side, 'SHOULDER', 'CLOSED'];
  if (c === 'G20-2') return ['END', 'ROAD WORK'];
  if (c === 'W20-2') return ['DETOUR', 'AHEAD'];
  if (c === 'W20-3') return ['ROAD', 'CLOSED', 'AHEAD'];
  if (c === 'W20-4') return ['ONE LANE', 'ROAD', 'AHEAD'];
  if (c === 'W3-4') return ['BE', 'PREPARED', 'TO STOP'];
  if (c === 'W21-1A') return ['WORKERS', 'AHEAD'];
  if (c === 'R11-2') return ['ROAD', 'CLOSED'];
  if (c === 'R9-9') return ['SIDEWALK', 'CLOSED'];
  if (c === 'R9-11A') return ['SIDEWALK CLOSED', 'USE OTHER SIDE'];
  return String(text || code || '').replace(/\(.*?\)/g, '').trim().split(/\s+/).filter(Boolean).slice(0, 3);
}

// Fit lines into a shape: avail(dy) = the width free at that height from the centre.
function fitLines(g, lines, cx, cy, lineH, avail) {
  const n = lines.length, top = cy - ((n - 1) * lineH) / 2;
  lines.forEach((line, i) => {
    const y = top + i * lineH;
    let fs = lineH * 0.82;
    g.font = `800 ${fs}px Arial, Helvetica, sans-serif`;
    const w = g.measureText(line).width, room = avail(y - cy);
    if (w > room) { fs *= room / w; g.font = `800 ${fs}px Arial, Helvetica, sans-serif`; }
    g.fillText(line, cx, y);
  });
}

function arrowHead(g, x, y, dir, size) {   // dir: 'up' | 'right'
  g.beginPath();
  if (dir === 'up') { g.moveTo(x, y - size); g.lineTo(x - size * 0.8, y + size * 0.2); g.lineTo(x + size * 0.8, y + size * 0.2); }
  else { g.moveTo(x + size, y); g.lineTo(x - size * 0.2, y - size * 0.8); g.lineTo(x - size * 0.2, y + size * 0.8); }
  g.closePath(); g.fill();
}

// Draw one sign centred on (cx, cy), `size` px across its widest.
export function drawSign(g, code, text, cx, cy, size) {
  const c = String(code || '').toUpperCase();
  g.save();
  g.textAlign = 'center'; g.textBaseline = 'middle';
  if (c === 'R3-1' || c === 'R3-2') {
    // No Right Turn: white square, black turn arrow, red circle and slash.
    const s = size * 0.82, h = s / 2;
    g.fillStyle = '#fff'; g.strokeStyle = BLACK; g.lineWidth = Math.max(1, s * 0.04);
    g.fillRect(cx - h, cy - h, s, s); g.strokeRect(cx - h, cy - h, s, s);
    g.strokeStyle = BLACK; g.fillStyle = BLACK; g.lineWidth = s * 0.09; g.lineJoin = 'round';
    const k = c === 'R3-2' ? -1 : 1;   // No Left Turn is the mirror image
    g.beginPath(); g.moveTo(cx - k * s * 0.12, cy + s * 0.3); g.lineTo(cx - k * s * 0.12, cy - s * 0.05); g.lineTo(cx + k * s * 0.12, cy - s * 0.05); g.stroke();
    if (k > 0) arrowHead(g, cx + s * 0.16, cy - s * 0.05, 'right', s * 0.11);
    else { g.save(); g.translate(cx - s * 0.16, cy - s * 0.05); g.scale(-1, 1); arrowHead(g, 0, 0, 'right', s * 0.11); g.restore(); }
    g.strokeStyle = RED; g.lineWidth = s * 0.075;
    g.beginPath(); g.arc(cx, cy, s * 0.37, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(cx - s * 0.26, cy - s * 0.26); g.lineTo(cx + s * 0.26, cy + s * 0.26); g.stroke();
  } else if (/^R(11-2|9-9|9-11A)$/.test(c)) {
    // White regulatory panels (ROAD CLOSED, SIDEWALK CLOSED): black words on white, wider than tall.
    const w = size, h = size * 0.62;
    g.fillStyle = '#fff'; g.strokeStyle = BLACK; g.lineWidth = Math.max(1, size * 0.03);
    g.fillRect(cx - w / 2, cy - h / 2, w, h); g.strokeRect(cx - w / 2, cy - h / 2, w, h);
    g.fillStyle = BLACK;
    const lines = wordsFor(c, text);
    fitLines(g, lines, cx, cy, h * (lines.length > 2 ? 0.27 : 0.32), () => w * 0.86);
  } else if (c === 'G20-2') {
    // END ROAD WORK: an orange panel twice as wide as it is tall.
    const w = size, h = size / 2;
    g.fillStyle = ORANGE; g.strokeStyle = BLACK; g.lineWidth = Math.max(1, size * 0.03);
    g.fillRect(cx - w / 2, cy - h / 2, w, h); g.strokeRect(cx - w / 2, cy - h / 2, w, h);
    g.lineWidth = Math.max(0.6, size * 0.015); g.strokeRect(cx - w / 2 + size * 0.04, cy - h / 2 + size * 0.04, w - size * 0.08, h - size * 0.08);
    g.fillStyle = BLACK;
    fitLines(g, wordsFor(c, text), cx, cy, h * 0.34, () => w * 0.82);
  } else {
    // Warning signs: an orange diamond, `size` corner to corner.
    const r = size / 2;
    const diamond = k => { g.beginPath(); g.moveTo(cx, cy - r * k); g.lineTo(cx + r * k, cy); g.lineTo(cx, cy + r * k); g.lineTo(cx - r * k, cy); g.closePath(); };
    g.fillStyle = ORANGE; diamond(1); g.fill();
    g.strokeStyle = BLACK; g.lineWidth = Math.max(1, size * 0.03); g.stroke();
    g.lineWidth = Math.max(0.6, size * 0.015); diamond(0.9); g.stroke();
    g.fillStyle = BLACK; g.strokeStyle = BLACK;
    if (c.startsWith('W4-2')) {
      // Lane Ends symbol: the through lane straight up, the closing lane bending into it.
      const m = /L$/.test(c) ? -1 : 1, lw = size * 0.07;
      g.lineWidth = lw; g.lineCap = 'butt';
      const xa = cx - m * size * 0.1, xb = cx + m * size * 0.12;
      g.beginPath(); g.moveTo(xa, cy + size * 0.3); g.lineTo(xa, cy - size * 0.16); g.stroke();
      arrowHead(g, xa, cy - size * 0.2, 'up', size * 0.09);
      g.beginPath(); g.moveTo(xb, cy + size * 0.3); g.lineTo(xb, cy + size * 0.06);
      g.quadraticCurveTo(xb, cy - size * 0.05, xa + m * size * 0.07, cy - size * 0.12); g.stroke();
    } else {
      const lines = wordsFor(c, text);
      fitLines(g, lines, cx, cy, size * (lines.length > 2 ? 0.13 : 0.15), dy => (r * 0.86 - Math.abs(dy) - size * 0.05) * 2 * 0.86);
    }
  }
  g.restore();
}

// Signs that can be added by hand on a drawing (+ Add device), in the order they are offered.
export const SIGN_CATALOG = [
  ['W20-1', 'ROAD WORK AHEAD'], ['W20-5R', 'RIGHT LANE CLOSED AHEAD'], ['W20-5L', 'LEFT LANE CLOSED AHEAD'],
  ['W4-2R', 'LANE ENDS (symbol)'], ['W4-2L', 'LANE ENDS (symbol)'], ['W21-5', 'SHOULDER CLOSED'],
  ['W20-4', 'ONE LANE ROAD AHEAD'], ['W20-3', 'ROAD CLOSED AHEAD'], ['W20-2', 'DETOUR AHEAD'], ['W3-4', 'BE PREPARED TO STOP'],
  ['W21-1a', 'WORKERS AHEAD'], ['R3-1', 'NO RIGHT TURN'], ['R3-2', 'NO LEFT TURN'], ['R11-2', 'ROAD CLOSED'],
  ['R9-9', 'SIDEWALK CLOSED'], ['R9-11a', 'SIDEWALK CLOSED USE OTHER SIDE'], ['G20-2', 'END ROAD WORK'],
];

// The sign as a PNG data URL, `px` square (transparent around the shape).
const cache = {};
export function signPng(code, text, px = 120) {
  const key = `${code}|${text}|${px}`;
  if (cache[key]) return cache[key];
  const cv = document.createElement('canvas'); cv.width = px; cv.height = px;
  drawSign(cv.getContext('2d'), code, text, px / 2, px / 2, px * 0.96);
  return (cache[key] = cv.toDataURL('image/png'));
}
